import Select from 'components/Forms/Select/Select';
import { mapDeliveryAddresses } from 'connectors/customer/CurrentCustomer';
import {
    useCurrentCustomerUserDeliveryAddressesQueryApi,
    useSelectDefaultDeliveryAddressMutationApi,
} from 'graphql/generated';
import { canUseLocation } from 'helpers/canUseDom';
import { PAGE_QUERY_PARAMETER_NAME } from 'helpers/queryParams/queryParamNames';
import useTranslation from 'next-translate/useTranslation';
import { useRouter } from 'next/router';
import { useState } from 'react';
import { CurrentCustomerType, DeliveryAddressType } from 'types/customer';
import { SelectOptionType } from 'types/selectOptions';

type DeliveryAddressSelectorProps = {
    currentCustomerUser: CurrentCustomerType;
};

const VALUE_SEPARATOR = '-|-';

export const DeliveryAddressSelector: FC<DeliveryAddressSelectorProps> = ({ currentCustomerUser }) => {
    const { t } = useTranslation();
    const router = useRouter();

    const [isSelectorFocused, setIsSelectorFocused] = useState(false);

    const [, selectDefaultDeliveryAddress] = useSelectDefaultDeliveryAddressMutationApi();
    const [{ data: deliveryAddresses, fetching: isDeliveryAddressesQueryRunning }] =
        useCurrentCustomerUserDeliveryAddressesQueryApi({
            pause: !isSelectorFocused,
        });
    const mappedDeliveryAddresses = mapDeliveryAddresses(deliveryAddresses?.currentCustomerUserDeliveryAddresses ?? []);

    const formatSelectedAddressLabel = (deliveryAddress: DeliveryAddressType) => {
        return `<b>${deliveryAddress.companyName}${
            deliveryAddress.companyName2 ? ', ' + deliveryAddress.companyName2 : ''
        }</b>&nbsp;<small>${deliveryAddress.city}, ${deliveryAddress.street} &nbsp;${t('ID')}: ${
            deliveryAddress.sapId
        }</small>`;
    };

    const formatSelectOptionLabel = (deliveryAddress: DeliveryAddressType) => {
        return `
            <div class="flex flex-col md:flex-row md:justify-between md:gap-4">
              <b class="md:basis-2/5">${deliveryAddress.companyName}${
            deliveryAddress.companyName2 ? ', ' + deliveryAddress.companyName2 : ''
        }</b>
              <span class="text-small md:text-base md:basis-2/5">${deliveryAddress.city}, ${
            deliveryAddress.street
        }</span>
              <span class="text-small md:text-base md:basis-1/5">${t('ID')}: ${deliveryAddress.sapId}</span>
            </div>
          `;
    };

    const mapDeliveryAddressToOption = (deliveryAddress: DeliveryAddressType): SelectOptionType => {
        const label = formatSelectOptionLabel(deliveryAddress);

        const value = [
            deliveryAddress.uuid,
            deliveryAddress.sapId,
            deliveryAddress.companyName,
            deliveryAddress.companyName2,
            deliveryAddress.city,
        ].join(VALUE_SEPARATOR);

        return {
            value: value,
            label: label,
        };
    };

    const options: SelectOptionType[] = (
        mappedDeliveryAddresses.length > 0 ? mappedDeliveryAddresses : currentCustomerUser.deliveryAddresses
    ).map(mapDeliveryAddressToOption);

    const onDeliveryAddressChange = async (newDeliveryAddressUuid: string | null) => {
        if (!newDeliveryAddressUuid) {
            return;
        }

        const changeDeliveryAddressResult = await selectDefaultDeliveryAddress({
            deliveryAddressUuid: newDeliveryAddressUuid,
        });

        if (changeDeliveryAddressResult.data?.SelectDefaultDeliveryAddress) {
            // Reload header, prices, stocks, ...
            if (canUseLocation()) {
                const url = new URL(window.location.href);
                if (url.searchParams.has(PAGE_QUERY_PARAMETER_NAME)) {
                    url.searchParams.delete(PAGE_QUERY_PARAMETER_NAME);
                    router.replace(url.pathname + url.search).then(() => {
                        router.reload();
                    });

                    return;
                }
            }

            router.reload();
        }
    };

    return (
        <Select
            id={`deliveryAddressSelector-${currentCustomerUser.uuid}`}
            onFocus={() => setIsSelectorFocused(true)}
            isLoading={isDeliveryAddressesQueryRunning}
            formatOptionLabel={(data) => {
                return <span dangerouslySetInnerHTML={{ __html: (data as SelectOptionType).label }} />;
            }}
            filterOption={(option: SelectOptionType, inputValue: string) =>
                option.value.toLowerCase().includes(inputValue.toLowerCase())
            }
            className="w-full focus-within:z-aboveOverlay vl:w-[680px] xl:w-[830px]"
            isSearchable
            options={options}
            onChange={(option) => onDeliveryAddressChange((option as SelectOptionType).value.split(VALUE_SEPARATOR)[0])}
            hasError={false}
            label={t('Delivery address')}
            value={
                currentCustomerUser.defaultDeliveryAddress
                    ? {
                          value: currentCustomerUser.defaultDeliveryAddress.sapId,
                          label: formatSelectedAddressLabel(currentCustomerUser.defaultDeliveryAddress),
                      }
                    : null
            }
        />
    );
};
